import { useEffect, useRef, useState } from "react";

// Protege un formulario/editor con cambios reales sin guardar. `hayCambios`
// lo calcula quien llama (comparación contra el estado inicial — nunca se
// muestra la advertencia si no hubo modificaciones reales).
//
// - `intentarSalir(accion)`: envolvé cualquier acción que abandone el
//   formulario (Volver, Cancelar, cerrar el modal, cambiar de pestaña) con
//   esto. Si no hay cambios, `accion` se ejecuta directo; si los hay, se
//   muestra la confirmación y `accion` sólo corre si el usuario elige
//   "Salir sin guardar".
// - Mientras `hayCambios` es true, un `beforeunload` nativo protege contra
//   cerrar la pestaña/recargar (el texto lo define el navegador, no es
//   personalizable — así funciona la API en todos los navegadores modernos).
// - El diálogo en sí se arma con el `ConfirmacionDialog` ya existente en el
//   componente que use este hook (no se crea un componente de diálogo
//   nuevo): `abierto={mostrandoConfirmacion}`, `onConfirmar={confirmarSalida}`,
//   `onCancelar={seguirEditando}`.
export function useCambiosSinGuardar(hayCambios: boolean) {
  const accionPendiente = useRef<(() => void) | null>(null);
  const [mostrandoConfirmacion, setMostrandoConfirmacion] = useState(false);

  useEffect(() => {
    if (!hayCambios) return;
    function alIntentarCerrarVentana(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", alIntentarCerrarVentana);
    return () => window.removeEventListener("beforeunload", alIntentarCerrarVentana);
  }, [hayCambios]);

  function intentarSalir(accion: () => void) {
    if (!hayCambios) {
      accion();
      return;
    }
    accionPendiente.current = accion;
    setMostrandoConfirmacion(true);
  }

  function confirmarSalida() {
    const accion = accionPendiente.current;
    accionPendiente.current = null;
    setMostrandoConfirmacion(false);
    accion?.();
  }

  function seguirEditando() {
    accionPendiente.current = null;
    setMostrandoConfirmacion(false);
  }

  return { intentarSalir, mostrandoConfirmacion, confirmarSalida, seguirEditando };
}
